import React, {
  useState,
  useEffect,
  useCallback,
  useRef
} from "react";
import { useNavigate } from "react-router-dom";
import "./Gallery.css";
import AOS from "aos";
import "aos/dist/aos.css";

/* Swiper */
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";

const galleryImages = [
  {
    src: "/images/gallery/gallery2.webp",
    title: "Dr. Chavan Multispeciality Hospital",
  },
  { src: "/images/gallery/gallery1.webp", title: "Dr. Saurabh Chavan" },
  {
    src: "/images/gallery/gallery5.webp",
    title: "Dr. Shubhada Bahirat-Chavan",
  },
  {
    src: "/images/gallery/gallery3.webp",
    title: "FMAS - Dr. Shubhada Bahirat-Chavan",
  },
  { src: "/images/gallery/gallery4.webp", title: "FMAS - Dr. Saurabh Chavan" },
  { src: "/images/gallery/gallery6.webp", title: "Sonography Training" },
];

const Gallery = () => {
  const [currentIndex, setCurrentIndex] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const galleryRef = useRef(null);

  const navigate = useNavigate();

  /* INIT AOS */
  useEffect(() => {
    AOS.init({ duration: 900, once: true, easing: "ease-in-out" });
  }, []);

  /* Detect Mobile Screen */
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  /* Modal Functions */
  const openModal = useCallback((index) => setCurrentIndex(index), []);
  const closeModal = useCallback(() => setCurrentIndex(null), []);
  const nextImage = useCallback(
    () => setCurrentIndex((prev) => (prev + 1) % galleryImages.length),
    []
  );
  const prevImage = useCallback(
    () => setCurrentIndex((prev) => (prev === 0 ? galleryImages.length - 1 : prev - 1)),
    []
  );

  /* Keyboard Navigation */
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (currentIndex !== null) {
        if (e.key === "Escape") closeModal();
        if (e.key === "ArrowRight") nextImage();
        if (e.key === "ArrowLeft") prevImage();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [currentIndex, closeModal, nextImage, prevImage]);

  /* Scroll Lock */
  useEffect(() => {
    document.body.style.overflow = currentIndex !== null ? "hidden" : "auto";
  }, [currentIndex]);

  return (
    <div className="gallery" ref={galleryRef}>
      <section className="gallery-page">
        <h1 className="gallery-title" data-aos="fade-up">
          Photo Gallery
        </h1>

        <p className="gallery-subtitle" data-aos="fade-up" data-aos-delay="100">
          Moments captured from various initiatives and events of Dr. Chavan Multispeciality Hospital.
        </p>

        {/* MOBILE VIEW */}
        {isMobile && (
          <Swiper
            modules={[Pagination, Autoplay]}
            pagination={{ clickable: true }}
            spaceBetween={20}
            slidesPerView={1}
            className="gallery-swiper"
            touchRatio={1}
            autoplay={{ delay: 3000, disableOnInteraction: false }}
          >
            {galleryImages.map((img, index) => (
              <SwiperSlide key={index} onClick={() => openModal(index)}>
                <div className="gallery-slide">
                  <img src={img.src} alt={img.title} loading="lazy" />
                  <div className="gallery-caption">{img.title}</div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        )}

        {/* DESKTOP GRID */}
        {!isMobile && (
          <div className="gallery-grid">
            {galleryImages.map((img, index) => (
              <div
                className="gallery-item"
                key={index}
                data-aos="fade-up"
                data-aos-delay={index * 120}
                onClick={() => openModal(index)}
              >
                <img src={img.src} alt={img.title} loading="lazy" />
                <div className="gallery-caption">{img.title}</div>
              </div>
            ))}
          </div>
        )}

        {/* VIEW ALL BUTTON – FIXED */}
        <div className="view-all-container">
          <button
            className="view-all-button"
            onClick={() => navigate("/gallery-full")}
          >
            View All
          </button>
        </div>

        {/* MODAL */}
        {currentIndex !== null && (
          <div
            className="modal-overlay"
            onClick={closeModal}
            role="dialog"
            aria-modal="true"
          >
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <span className="modal-close" onClick={closeModal}>✕</span>

              <img
                src={galleryImages[currentIndex].src}
                alt={galleryImages[currentIndex].title}
              />

              <p className="modal-title">
                {galleryImages[currentIndex].title}
              </p>

              {/* NAV BUTTONS */}
              <button className="nav-btn left" onClick={prevImage}>❮</button>
              <button className="nav-btn right" onClick={nextImage}>❯</button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default Gallery;
